import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AttributesEditorDataSource } from '../components/attributes-editor/attributesEditorDataSource';
import { ModalService } from './modal.service';
import { Attribute } from '../../app/shared/models/attributes/attribute';
import { AttributeDefinition } from '../../app/shared/models/attributes/attributeDefinition';
import { LocalDataCacheService } from '../../app/shared/services/local-data-cache.service';
import { SnackBarService } from '../../app/shared/services/snackbar.service';
import { AttributeUiType } from '../../app/shared/models/attributes/attributeUiType';
import { ComplexAttributeSubFieldValue } from '../../app/shared/models/attributes/complexAttributeSubFieldValue';
import { AttributeDefinitionValueListItem } from '../../app/shared/models/attributes/attributeDefinitionValueListItem';
const LocalStorageExpirationDurationMs: number = 1000 * 60 * 60 * 24 * 15; // 15 days

@Injectable({
  providedIn: 'root'
})
export class LocalPersistenceService {
  buttonSubject: BehaviorSubject<MembersHeaderButton> = new BehaviorSubject<MembersHeaderButton>(
    null
  );

  constructor(
    private snackbarService: SnackBarService,
    private localDataCacheService: LocalDataCacheService,
    private dialog: ModalService
  ) {}

  deleteLocalData(id: string) {
    this.localDataCacheService.deleteObject(id);
    this.hideSaveForLatterButton();
  }

  hideSaveForLatterButton() {
    this.buttonSubject.next(null);
  }

  getLocalData = <T>(id: string): T => this.localDataCacheService.getObject(id) as T;

  saveDataForLater(localData, id: string, showMessage = true): void {
    if (this.localDataCacheService.isLocalStorageAvailable) {
      this.storeLocalData(localData, id, showMessage);
      if (!showMessage) return;

      this.dialog.openQuickModal(
        'You must use the same device and browser, and submit these responses in the next 15 days. Note: we cannot save responses if you are using your browser in private mode.',
        { header: 'Your responses have been saved', yesText: 'Ok', noText: '' }
      );
    } else if (showMessage) {
      this.snackbarService.open('You cannot save when your browser is in private mode.', false);
    }
  }

  private storeLocalData(data: any, id: string, showError: boolean): void {
    try {
      this.localDataCacheService.cacheObject(
        id,
        data,
        new Date(new Date().getTime() + LocalStorageExpirationDurationMs)
      );
    } catch (e) {
      if (showError) {
        this.snackbarService.open('Error saving responses', false);
        throw e;
      }
    }
  }

  getAnswers(attributesEditorDataSource: AttributesEditorDataSource): AnswerModel[] {
    if (attributesEditorDataSource.editable == null) {
      return []; //will happen if only system fields fields
    }

    let answers = attributesEditorDataSource.editable
      .filter((e) => !e.isReadOnly && !e.isHidden)
      .map((e) => {
        let attr = attributesEditorDataSource.attributes.find(
          (x) => x.attributeDefinitionId == e.attributeDefinitionId
        );

        let answer: AnswerModel = {
          questionId: e.attributeDefinitionId,
          responses: [],
          complexResponses: [],
          edited: (attr?.edited ?? false) || e.edited // depending on the flow one or the other spot may have the flag
        };

        if (e.type == AttributeUiType.Complex) {
          e.complexAttributeValues.forEach((c) => {
            c.items.forEach((i) => {
              if (i.allowsMultiple) {
                answer.complexResponses = answer.complexResponses.concat(
                  i.subFieldValues.map((x) => {
                    return {
                      groupId: c.groupId,
                      subFieldDefinitionItemId: x.subFieldDefinitionItemId,
                      name: x.name,
                      type: x.type,
                      valueList: {
                        id: x.attributeDefinitionValueListItem.id,
                        itemValue: x.attributeDefinitionValueListItem.itemValue
                      }
                    } as ComplexAnswerModel;
                  })
                );
              } else {
                if (i.subFieldValues[0].attributeDefinitionValueListItem) {
                  answer.complexResponses = answer.complexResponses.concat({
                    groupId: c.groupId,
                    subFieldDefinitionItemId: i.subFieldValues[0].subFieldDefinitionItemId,
                    name: i.subFieldValues[0].name,
                    type: i.subFieldValues[0].type,
                    valueList: {
                      id: i.subFieldValues[0].attributeDefinitionValueListItem.id,
                      itemValue: i.subFieldValues[0].attributeDefinitionValueListItem.itemValue
                    }
                  } as ComplexAnswerModel);
                } else {
                  answer.complexResponses = answer.complexResponses.concat({
                    groupId: c.groupId,
                    name: i.subFieldValues[0].name,
                    subFieldDefinitionItemId: i.subFieldValues[0].subFieldDefinitionItemId,
                    type: i.subFieldValues[0].type,
                    value: i.subFieldValues[0].value
                  } as ComplexAnswerModel);
                }
              }
            });
          });
        } else {
          answer.otherValue = e.otherValue;
          if (e.allowsMultipleValues) {
            answer.responses = e.valueList.map((x) => x.id);
          } else {
            if (e.attributeDefinitionValueListItem) {
              answer.responses = [e.attributeDefinitionValueListItem.id];
            } else {
              answer.responses = [e.value];
            }
          }
        }

        return answer;
      });

    return answers;
  }
  static buildFormUsingSavedAnswers(
    answers: AnswerModel[],
    questions: AttributeDefinition[]
  ): Attribute[] {
    let attributes: Attribute[] = [];
    answers.forEach((a) => {
      let formAttribute = questions.find((x) => x.id == a.questionId);

      if (formAttribute) {
        if (formAttribute.uiType == AttributeUiType.Complex) {
          a.complexResponses?.forEach((c) => {
            let attribute = Attribute.parseFromAttributeDefinition(formAttribute);
            attribute.edited = a.edited;
            attribute.complexAttributeSubFieldValue = ComplexAttributeSubFieldValue.parse({
              subFieldDefinitionItemId: c.subFieldDefinitionItemId,
              groupId: c.groupId,
              value: c.value ? c.value : c.valueList.itemValue,
              name: c.name,
              type: c.type,
              attributeDefinitionValueListItem: c.valueList
                ? AttributeDefinitionValueListItem.parse(c.valueList)
                : null
            });
            attributes.push(attribute);
          });
        } else if (formAttribute.valueList.length) {
          a.responses.forEach((v) => {
            let attribute = Attribute.parseFromAttributeDefinition(formAttribute);
            attribute.edited = a.edited;
            attribute.otherValue = a.otherValue;
            let value = attribute.valueList.find((x) => x.id == v);
            if (value) {
              attribute.attributeDefinitionValueListItem = value;
              attribute.value = value.itemValue;
              attributes.push(attribute);
            }
          });
        } else {
          let attribute = Attribute.parseFromAttributeDefinition(formAttribute);
          attribute.value = a.responses[0];
          attribute.edited = a.edited;
          attributes.push(attribute);
        }
      }
    });
    return attributes;
  }
}

export type MembersHeaderButton = {
  label: string;
  action: () => void;
  tooltip: string;
};

export interface LocalData<T> {
  reference: T;
  answers: AnswerModel[];
  team?: { teamName: string; teamPublicName: string };
  /**
   * positionDefinitionId -> personId -> seat (tracked) field answers. Stored as AnswerModel rather
   * than the submit shape so it round-trips through buildFormUsingSavedAnswers like every other
   * saved answer, including multi-value fields.
   */
  positionSeatFieldAnswers?: Record<string, Record<string, AnswerModel[]>>;
}

export interface AnswerModel {
  questionId: string;
  responses: string[];
  complexResponses?: ComplexAnswerModel[];
  edited: boolean;
  otherValue?: string;
}

export interface ComplexAnswerModel {
  groupId: number;
  subFieldDefinitionItemId: string;
  name: string;
  type: string;
  value: string;
  valueList: {
    id: string;
    itemValue: string;
  };
}
